import { Flex, Textarea } from '@chakra-ui/core'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'
import useLocaleText from 'components/useLocaleText'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { boxScaleEffect } from 'components/PopOverview/AI/StartConversationsBlock'
import { IoSend, IoMic } from 'react-icons/io5'
import { isRtlSelector } from 'redux/selectors/general'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition'

const pulseAnimation = `
  @keyframes pulse {
    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(225, 29, 72, 0.7); }
    70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(225, 29, 72, 0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(225, 29, 72, 0); }
  }
`

const getSpeechLanguage = (langCode: string) => {
  const map: Record<string, string> = {
    en: 'en-US',
    he: 'he-IL',
    es: 'es-ES',
    it: 'it-IT',
    pt: 'pt-PT',
    ru: 'ru-RU',
    lt: 'lt-LT',
    lv: 'lv-LV',
    tr: 'tr-TR',
    bg: 'bg-BG',
    'pt-BR': 'pt-BR',
  }
  if (map[langCode]) return map[langCode]
  const short = langCode?.split('-')[0]
  if (short && map[short]) return map[short]
  return 'en-US'
}

interface IProps {
  onSend: (message: string) => void | Promise<void>
  disabled?: boolean
  onStopVoicePlayback?: () => void
}

type StopReason = 'manual' | 'send' | 'unmount' | null

export const ConversationFooter = ({ onSend, disabled = false, onStopVoicePlayback }: IProps) => {
  const isRtl = useSelector(isRtlSelector)
  const theme = useSelector((state: RootState) => state.general.theme)
  const locale = useSelector((state: RootState) => state.general.locale)

  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')

  const currentSpeechLang = useMemo(() => getSpeechLanguage(locale), [locale])
  const t_placeholder = useLocaleText('write_a_comment')

  const {
    transcript,
    interimTranscript,
    listening,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable,
    resetTranscript,
  } = useSpeechRecognition()

  const speechSupported = browserSupportsSpeechRecognition

  const acceptSpeechRef = useRef(false)
  const appendModeRef = useRef(false)
  const baseTextRef = useRef('')
  const lastStopReasonRef = useRef<StopReason>(null)

  const startListening = useCallback(async () => {
    if (!speechSupported) return
    try {
      // Stop bot voice playback when user starts speaking
      onStopVoicePlayback?.()

      appendModeRef.current = lastStopReasonRef.current === 'manual' && !!message.trim()
      baseTextRef.current = appendModeRef.current ? message : ''

      resetTranscript()
      acceptSpeechRef.current = true
      await SpeechRecognition.startListening({ continuous: true, language: currentSpeechLang })
    } catch (e) {
      console.error('Speech start error', e)
    }
  }, [currentSpeechLang, message, resetTranscript, speechSupported, onStopVoicePlayback])

  const stopListening = useCallback(() => {
    if (!speechSupported) return
    try {
      acceptSpeechRef.current = false
      SpeechRecognition.stopListening()
      lastStopReasonRef.current = 'manual'
    } catch (e) {
      console.error('Speech stop error', e)
    }
  }, [speechSupported])

  const typeMessage = (e: React.ChangeEvent<HTMLInputElement>) => {
    acceptSpeechRef.current = false
    setMessage(e.target.value)
  }

  useEffect(() => {
    if (!listening || !acceptSpeechRef.current) return
    const currentText = (interimTranscript || transcript || '').trim()
    if (!currentText) return

    if (appendModeRef.current) {
      setMessage(baseTextRef.current + currentText)
    } else {
      setMessage(currentText)
    }
  }, [transcript, interimTranscript, listening])

  const handleSend = async () => {
    const text = message.trim()
    if (!text || disabled) return

    if (listening) {
      try {
        acceptSpeechRef.current = false
        await SpeechRecognition.stopListening()
      } catch (e) {
        console.error('Speech stop error', e)
      }
    }

    lastStopReasonRef.current = 'send'
    appendModeRef.current = false
    baseTextRef.current = ''

    setIsLoading(true)
    try {
      await Promise.resolve(onSend(text))
      setMessage('')
      resetTranscript()
    } catch (e) {
      console.error('Send error', e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    return () => {
      try {
        SpeechRecognition.abortListening()
      } catch (e) {
        console.error('Speech abort error', e)
      } finally {
        lastStopReasonRef.current = 'unmount'
        acceptSpeechRef.current = false
        appendModeRef.current = false
        baseTextRef.current = ''
      }
    }
  }, [])

  const isSendDisabled = isLoading || !message || disabled
  const isMicrophoneDisabled = isLoading || disabled || isMicrophoneAvailable === false

  return (
    <>
      <style>{pulseAnimation}</style>
      <Flex alignItems="center" w="100%" justifyContent="space-between" style={{ gap: '10px' }} pb="15px" px="15px">
        <Textarea
          flex={1}
          minW="80%"
          borderRadius="10px"
          value={message}
          resize="none"
          minH="60px"
          maxH="120px"
          onChange={typeMessage}
          placeholder={t_placeholder}
          _focus={{
            borderColor: theme?.elementsColor,
            boxShadow: `0 0 0 1px ${theme?.elementsColor}`,
            outline: 'none',
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSend()
            }
          }}
        />

        {speechSupported && (
          <NoFocusButton
            borderRadius="50%"
            onClick={listening ? stopListening : startListening}
            isDisabled={isMicrophoneDisabled}
            css={boxScaleEffect}
            color="white"
            p="10px"
            backgroundColor={listening ? '#E11D48' : theme?.elementsColor}
            _hover={{ opacity: 0.8 }}
            style={{
              animation: listening ? 'pulse 1.5s ease-in-out infinite' : 'none',
              boxShadow: listening ? '0 0 20px rgba(225, 29, 72, 0.5)' : 'none',
            }}
            aria-label={listening ? 'Stop recording' : 'Start recording'}
            title={
              isMicrophoneAvailable === false
                ? 'Microphone is not available, check permissions'
                : listening
                ? 'Stop recording (text appears automatically)'
                : `Start recording (${currentSpeechLang}) - text appears automatically`
            }
          >
            <IoMic size={22} />
          </NoFocusButton>
        )}

        <NoFocusButton
          borderRadius="50%"
          onClick={handleSend}
          isDisabled={isSendDisabled}
          css={boxScaleEffect}
          color="white"
          p="10px"
          backgroundColor={theme?.elementsColor}
          _hover={{ opacity: 0.8 }}
          title="Send"
        >
          <IoSend
            size={22}
            style={{
              transform: isRtl ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s ease',
            }}
          />
        </NoFocusButton>
      </Flex>
    </>
  )
}
